// Grouping the message centre BY PERSON — CCT-347.
//
// ⚠️ IN A MODULE OF ITS OWN SO IT CAN BE EXECUTED. Kept inside the screen, the rule could only be
// tested by transcribing it into the test — and a transcription proves nothing: mutating the
// screen left the guard green. It lives here, the screen imports it, the test imports it too.
//
// The message centre has TWO sources — the author's conversations and the threads the engine
// opened around a task — and the player thinks in neither. They think in PEOPLE.

/** What the grouping needs of a conversation. Each source normalises into this shape. */
export interface ChatEntry {
  /** Stable key of the row (source + identifier): React needs it, and so does deduplication. */
  key: string;
  personKey: string;
  /** Last activity, ISO. The authored conversations carry none — they sort last. */
  at: string | null;
}

export interface ChatPerson<T extends ChatEntry> {
  key: string;
  chats: T[];
}

/**
 * The key of the PERSON a conversation belongs to.
 *
 * ⚠️ IDENTITY FIRST, LABEL ONLY AS A FALLBACK. Grouping on the name alone merges two different
 * characters who happen to share one — and splits one person in two on a spelling or a stray
 * space. The identifier is missing only when the conversation names somebody outside the
 * mission's cast; the name is then all there is, so it is normalised before being used.
 */
export function personKey(id: number | null | undefined, name: string | null | undefined): string {
  return id != null ? `id:${id}` : `name:${(name || '').trim().toLowerCase()}`;
}

const time = (at: string | null) => (at ? new Date(at).getTime() : 0);

/**
 * Conversations grouped by person, most recently active person first, and inside each person the
 * most recent conversation first.
 *
 * An entry with no timestamp sorts last within its group rather than jumping to the top: an
 * authored conversation nobody has touched must not push aside the thread that answered this
 * morning.
 */
export function groupByPerson<T extends ChatEntry>(entries: readonly T[]): ChatPerson<T>[] {
  const byPerson = new Map<string, T[]>();
  for (const c of entries) byPerson.set(c.personKey, [...(byPerson.get(c.personKey) ?? []), c]);
  return [...byPerson.entries()]
    .map(([key, chats]) => ({ key, chats: [...chats].sort((a, b) => time(b.at) - time(a.at)) }))
    .sort((a, b) => time(b.chats[0]?.at ?? null) - time(a.chats[0]?.at ?? null));
}
